/*
 * SoundManager - synthesizes all game audio with the Web Audio API.
 * No external audio files required (Chrome-friendly, instant load).
 * Audio context is created/resumed only after a user gesture (ignition).
 */

class SoundManager {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.engineNodes = null;
    this.engineRunning = false;
    this.muted = typeof window !== 'undefined' && localStorage.getItem('advit_muted') === '1';
    this.volume = 0.85;
  }

  async init() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') {
        try { await this.ctx.resume(); } catch (e) {}
      }
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(this.ctx.destination);
  }

  resume() {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
  }

  isMuted() {
    return this.muted;
  }

  setMuted(m) {
    this.muted = m;
    try { localStorage.setItem('advit_muted', m ? '1' : '0'); } catch (e) {}
    if (this.ctx && this.master) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(m ? 0 : this.volume, t, 0.05);
    }
    return this.muted;
  }

  startEngine() {
    if (!this.ctx || this.engineRunning) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const engineGain = ctx.createGain();
    engineGain.gain.value = 0;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    filter.Q.value = 6;

    const osc1 = ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc1.frequency.value = 55;
    const osc2 = ctx.createOscillator();
    osc2.type = 'sawtooth';
    osc2.frequency.value = 82;
    const osc3 = ctx.createOscillator();
    osc3.type = 'square';
    osc3.frequency.value = 40;

    // rumble LFO modulating osc1 frequency
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 16;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 18;
    lfo.connect(lfoGain);
    lfoGain.connect(osc1.frequency);

    osc1.connect(filter);
    osc2.connect(filter);
    osc3.connect(filter);
    filter.connect(engineGain);
    engineGain.connect(this.master);

    osc1.start();
    osc2.start();
    osc3.start();
    lfo.start();

    // ignition: quick rev then settle to idle
    engineGain.gain.setValueAtTime(0, t);
    engineGain.gain.linearRampToValueAtTime(0.22, t + 0.25);
    engineGain.gain.setTargetAtTime(0.16, t + 0.4, 0.3);

    this.engineNodes = { engineGain, filter, osc1, osc2, osc3, lfo, lfoGain };
    this.engineRunning = true;
  }

  setEngineIntensity(norm) {
    if (!this.ctx || !this.engineNodes) return;
    const n = Math.max(0, Math.min(1, norm));
    const t = this.ctx.currentTime;
    const { osc1, osc2, osc3, filter, engineGain } = this.engineNodes;
    const base = 55 + n * 85;
    osc1.frequency.setTargetAtTime(base, t, 0.08);
    osc2.frequency.setTargetAtTime(base * 1.5, t, 0.08);
    osc3.frequency.setTargetAtTime(base * 0.72, t, 0.08);
    filter.frequency.setTargetAtTime(450 + n * 2400, t, 0.08);
    engineGain.gain.setTargetAtTime(0.15 + n * 0.16, t, 0.1);
  }

  stopEngine() {
    if (!this.ctx || !this.engineNodes) return;
    const t = this.ctx.currentTime;
    const { engineGain, osc1, osc2, osc3, lfo } = this.engineNodes;
    engineGain.gain.setTargetAtTime(0, t, 0.2);
    [osc1, osc2, osc3, lfo].forEach((o) => {
      try { o.stop(t + 0.6); } catch (e) {}
    });
    this.engineNodes = null;
    this.engineRunning = false;
  }

  _blip({ type = 'square', from = 180, to = 90, dur = 0.14, gain = 0.3 }) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  gearShift() {
    // mechanical clunk = short low thud + click
    this._blip({ type: 'square', from: 150, to: 70, dur: 0.1, gain: 0.25 });
    this._blip({ type: 'triangle', from: 320, to: 180, dur: 0.06, gain: 0.12 });
  }

  horn() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const dur = 0.55;
    [370, 466].forEach((f) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.03);
      g.gain.setValueAtTime(0.18, t + dur - 0.1);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g);
      g.connect(this.master);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    });
  }

  arrivalChime() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => {
      const t = ctx.currentTime + i * 0.13;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.connect(g);
      g.connect(this.master);
      osc.start(t);
      osc.stop(t + 0.42);
    });
  }

  // bright two-tone "ding" played when a party detail is unlocked
  ding() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    [880, 1320].forEach((f) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.22, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      osc.connect(g);
      g.connect(this.master);
      osc.start(t);
      osc.stop(t + 0.75);
    });
  }

  // celebratory crowd "cheer" = noise swell + rising whistle, topped with the chime
  cheer() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const dur = 1.5;

    // crowd swell (filtered white noise)
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 0.7;
    bp.frequency.setValueAtTime(650, t);
    bp.frequency.linearRampToValueAtTime(1900, t + 0.55);
    bp.frequency.linearRampToValueAtTime(1300, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.35);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.95);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(this.master);
    src.start(t);
    src.stop(t + dur);

    // rising party whistle
    const w = ctx.createOscillator();
    const wg = ctx.createGain();
    w.type = 'sine';
    w.frequency.setValueAtTime(700, t + 0.05);
    w.frequency.exponentialRampToValueAtTime(1800, t + 0.45);
    w.frequency.exponentialRampToValueAtTime(1200, t + 0.7);
    wg.gain.setValueAtTime(0.0001, t + 0.05);
    wg.gain.exponentialRampToValueAtTime(0.1, t + 0.15);
    wg.gain.exponentialRampToValueAtTime(0.0001, t + 0.75);
    w.connect(wg);
    wg.connect(this.master);
    w.start(t + 0.05);
    w.stop(t + 0.8);

    // sparkle chime on top
    this.arrivalChime();
  }
}

const soundManager = new SoundManager();
export default soundManager;
